import React from "react";
import { Alert, Button, Card, Space, Typography, message } from "antd";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { acceptCollaboratorInvite, getCollaboratorInvite } from "../api";
import type { CollaboratorInvitePublic } from "./types";
import { useAuthStatus } from "../auth/AuthStatus";
import keycloak, { loginToApp } from "../keycloak";

export function CollaboratorPublicPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const { authReady } = useAuthStatus();
  const [invite, setInvite] = React.useState<CollaboratorInvitePublic | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [accepting, setAccepting] = React.useState(false);
  const authenticated = Boolean(keycloak.authenticated);

  const reload = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setInvite(await getCollaboratorInvite(token));
    } catch {
      message.error(t("collaborator.inviteNotFound"));
      setInvite(null);
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  if (!token) return null;

  const accepted = Boolean(invite?.accepted_at);

  return (
    <div className="collaborator-invite-page" data-testid="collaborator-invite-page">
      <Typography.Title level={3}>{t("collaborator.title")}</Typography.Title>
      <Typography.Paragraph type="secondary">{t("collaborator.intro")}</Typography.Paragraph>
      <Card loading={loading}>
        {invite && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Alert
              type={accepted ? "success" : "info"}
              showIcon
              message={
                accepted
                  ? t("collaborator.alreadyAccepted")
                  : t("collaborator.pendingInvite", { email: invite.invite_email })
              }
            />
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              <strong>{t("collaborator.caseLabel")}:</strong>{" "}
              {invite.case_reference || invite.case_id}
              <br />
              <strong>{t("compliance.columns.commodity")}:</strong> {invite.commodity}
              <br />
              <strong>{t("compliance.columns.supplier")}:</strong> {invite.supplier_name}
              <br />
              <strong>{t("common.expires")}:</strong>{" "}
              {new Date(invite.expires_at).toLocaleString()}
            </Typography.Paragraph>
            {!accepted && authReady && !authenticated && (
              <Button
                type="primary"
                onClick={() => loginToApp(`/collaborator/${token}`)}
                data-testid="collaborator-login"
              >
                {t("collaborator.signInToAccept")}
              </Button>
            )}
            {!accepted && authenticated && (
              <Button
                type="primary"
                loading={accepting}
                data-testid="collaborator-accept"
                onClick={async () => {
                  setAccepting(true);
                  try {
                    setInvite(await acceptCollaboratorInvite(token));
                    message.success(t("collaborator.accepted"));
                  } catch {
                    message.error(t("collaborator.acceptFailed"));
                  } finally {
                    setAccepting(false);
                  }
                }}
              >
                {t("collaborator.accept")}
              </Button>
            )}
            {accepted && (
              <Link to={`/dds/cases/${invite.case_id}`}>
                <Button type="primary" data-testid="collaborator-open-case">
                  {t("collaborator.openCase")}
                </Button>
              </Link>
            )}
          </Space>
        )}
      </Card>
    </div>
  );
}
